import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  trendingCategories,
  contentBlocks,
  socialCenterDashboardStats,
  livePulseItems,
  getHighestTrendingItem,
  type TrendingItem,
  type ContentBlock,
} from '@/mocks/rfwSocialCenter';

function TrendingItemCard({ item }: { item: TrendingItem }) {
  return (
    <Link
      to={item.link}
      className="flex items-center gap-4 p-4 rounded-2xl bg-background-50 border border-background-200/70 hover:border-rose-400/50 transition-all duration-300 group"
    >
      <div className="relative w-16 h-16 rounded-xl overflow-hidden shrink-0">
        <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover object-top" />
        <div className="absolute top-1 left-1 w-6 h-6 rounded-lg bg-background-50/90 flex items-center justify-center text-xs font-bold text-foreground-950 backdrop-blur-sm">
          {item.position}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <p className="text-sm font-bold text-foreground-950 line-clamp-1 group-hover:text-primary-600 transition-colors duration-200">{item.name}</p>
          <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${item.trendDirection === 'up' ? 'text-emerald-600' : item.trendDirection === 'down' ? 'text-rose-600' : 'text-foreground-400'} shrink-0`}>
            <i className={`${item.trendDirection === 'up' ? 'ri-arrow-up-line' : item.trendDirection === 'down' ? 'ri-arrow-down-line' : 'ri-subtract-line'} text-[9px]`}></i>
            {item.trendValue}
          </span>
        </div>
        <p className="text-[11px] text-foreground-500 line-clamp-2 leading-relaxed mb-2">{item.description}</p>
        <div className="flex items-center gap-3 text-[10px] text-foreground-400">
          {item.stats.map((stat, i) => (
            <span key={i} className="flex items-center gap-1">
              <strong className="text-foreground-700">{stat.value}</strong> {stat.label}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

function BlockItemCard({ item }: { item: ContentBlock['items'][0] }) {
  return (
    <Link
      to={item.link}
      className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-amber-400/50 transition-all duration-300 group"
    >
      <div className="relative w-14 h-14 rounded-lg overflow-hidden shrink-0">
        <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover object-top" />
        <div className="absolute top-1 left-1 w-5 h-5 rounded-md bg-background-50/90 flex items-center justify-center text-[10px] font-bold text-foreground-950 backdrop-blur-sm">
          {item.position}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-foreground-950 line-clamp-2 mb-1 group-hover:text-primary-600 transition-colors duration-200">{item.title}</p>
        <div className="flex items-center gap-2 text-[10px]">
          <span className="inline-flex items-center gap-1 text-foreground-400">
            <i className="ri-bar-chart-fill text-[9px] text-amber-500"></i>
            <strong className="text-foreground-700">{item.metricValue.toLocaleString()}</strong> {item.metricLabel}
          </span>
          <span className="text-foreground-300">·</span>
          <span className="text-foreground-400">{item.category}</span>
        </div>
      </div>
    </Link>
  );
}

export default function CentroSocialSection() {
  const [activeTab, setActiveTab] = useState<'trending' | 'bloques' | 'pulso'>('trending');
  const [selectedCategory, setSelectedCategory] = useState(trendingCategories[0].id);
  const [selectedBlock, setSelectedBlock] = useState(contentBlocks[0].id);

  const activeCategory = trendingCategories.find((c) => c.id === selectedCategory) || trendingCategories[0];
  const activeBlock = contentBlocks.find((b) => b.id === selectedBlock) || contentBlocks[0];
  const topItem = getHighestTrendingItem();

  const tabs = [
    { id: 'trending' as const, label: 'Tendencias', icon: 'ri-fire-line' },
    { id: 'bloques' as const, label: 'Lo Más', icon: 'ri-bar-chart-grouped-line' },
    { id: 'pulso' as const, label: 'Pulso', icon: 'ri-pulse-line' },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            CENTRO SOCIAL RFW
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            El Punto de Encuentro de <span className="text-primary-600">Toda la Comunidad</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Radios, locutores, programas, clubes, eventos y concursos. Todo lo que está pasando en RFW ahora mismo, en un solo lugar. Actualizado automáticamente en tiempo real.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-10">
          {livePulseItems.map((pulse, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${pulse.bgColor} flex items-center justify-center shrink-0`}>
                <i className={`${pulse.icon} ${pulse.color} text-sm`}></i>
              </div>
              <div>
                <p className="text-base font-bold text-foreground-950">{pulse.value}</p>
                <p className="text-[10px] text-foreground-500">{pulse.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'trending' && (
          <div>
            <div className="flex items-center gap-2 mb-6 flex-wrap">
              {trendingCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    selectedCategory === cat.id
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} text-[10px]`}></i>
                  {cat.name}
                  <span className={`text-[9px] opacity-80`}>({cat.totalItems.toLocaleString()})</span>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {activeCategory.items.map((item) => (
                <TrendingItemCard key={item.id} item={item} />
              ))}
            </div>
            <div className="text-center mt-6">
              <Link
                to="/centro-social"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
              >
                <i className="ri-fire-line"></i>
                Ver Todas las Tendencias
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        )}

        {activeTab === 'bloques' && (
          <div>
            <div className="flex items-center gap-2 mb-6 flex-wrap">
              {contentBlocks.map((block) => (
                <button
                  key={block.id}
                  onClick={() => setSelectedBlock(block.id)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    selectedBlock === block.id
                      ? 'bg-accent-500 text-white shadow-sm'
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  <i className={`${block.icon} text-[10px]`}></i>
                  {block.name}
                </button>
              ))}
            </div>
            <p className="text-xs text-foreground-500 mb-4">{activeBlock.description}</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {activeBlock.items.map((item) => (
                <BlockItemCard key={item.id} item={item} />
              ))}
            </div>
            <div className="text-center mt-6">
              <Link
                to="/centro-social"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 hover:bg-accent-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
              >
                <i className="ri-bar-chart-grouped-line"></i>
                Ver Todos los Rankings
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        )}

        {activeTab === 'pulso' && (
          <div className="space-y-5">
            <div className="p-6 rounded-2xl bg-accent-100/60 border border-accent-200/50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center">
                  <span className="w-3 h-3 rounded-full bg-white animate-pulse"></span>
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground-950">Comunidad Viva Ahora</p>
                  <p className="text-[10px] text-foreground-500">Actualizado hace {socialCenterDashboardStats.updatedMinutesAgo} minutos</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Total Tendencias', value: socialCenterDashboardStats.totalTrendingItems.toLocaleString(), icon: 'ri-fire-line', color: 'text-rose-600' },
                  { label: 'Categorías Activas', value: socialCenterDashboardStats.activeCategories, icon: 'ri-price-tag-3-line', color: 'text-amber-600' },
                  { label: 'Score Promedio', value: socialCenterDashboardStats.avgTrendScore, icon: 'ri-line-chart-line', color: 'text-emerald-600' },
                  { label: 'Más Activa', value: socialCenterDashboardStats.mostActiveCategory, icon: 'ri-trophy-line', color: 'text-violet-600', sub: socialCenterDashboardStats.mostActiveCategoryCount.toLocaleString() + ' items' },
                ].map((stat, i) => (
                  <div key={i} className="p-4 rounded-xl bg-white border border-background-200/70">
                    <div className={`w-8 h-8 rounded-lg ${stat.color.replace('text-', 'bg-').replace('600', '50')} flex items-center justify-center mb-2`}>
                      <i className={`${stat.icon} ${stat.color} text-sm`}></i>
                    </div>
                    <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                    <p className="text-[10px] text-foreground-500">{stat.label}</p>
                    {stat.sub && <p className="text-[9px] text-foreground-400">{stat.sub}</p>}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { title: 'Actualización Automática', icon: 'ri-refresh-line', desc: 'Todo lo que ves en el Centro Social se actualiza según la actividad real de la comunidad, sin intervención manual.' },
                { title: 'Tendencia Real', icon: 'ri-fire-line', desc: 'El algoritmo detecta picos de actividad y los refleja al instante. Lo que está en tendencia es lo que realmente está pasando.' },
                { title: 'Transparencia Total', icon: 'ri-eye-line', desc: 'Cada métrica tiene una fuente clara. Sabés exactamente por qué algo está en tendencia.' },
              ].map((rule, i) => (
                <div key={i} className="p-5 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className={`${rule.icon} text-lg text-accent-600`}></i>
                  </div>
                  <p className="text-sm font-bold text-foreground-950 mb-1">{rule.title}</p>
                  <p className="text-[11px] text-foreground-500">{rule.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 p-6 rounded-2xl bg-primary-100/70 border border-primary-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-flashlight-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Todo debe actualizarse automáticamente según actividad real.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            El Centro Social RFW no es un escaparate estático. Cada like, cada comentario, cada compartir, cada visita — todo suma y modifica lo que ves en tiempo real. Lo que es tendencia ahora mismo es lo que la comunidad está viviendo ahora mismo. Sin edición, sin curaduría manual, sin demoras. La verdad de la comunidad, al instante.
          </p>
        </div>
      </div>
    </section>
  );
}